import { useCallback, useState } from 'react';
import { useAITokensStore } from '@/store/ai-tokens';
import { useAuthStore } from '@/store/auth';
import { toast } from '@/components/ui/toast';
import { XCircle } from 'lucide-react';

/**
 * Hook to check AI credit before any AI action
 * @returns Functions and state for AI credit check
 */
export const useAICredit = () => {
  const { user } = useAuthStore();
  const { 
    checkTokens, 
    availableTokens,
    isLoading,
    hasChecked,
  } = useAITokensStore();
  
  const [showingWarning, setShowingWarning] = useState(false);

  /**
   * Check if user has enough AI credits
   * @returns true if user has credits, false otherwise
   */
  const checkCredit = useCallback(async () => {
    // User not logged in
    if (!user?.id) {
      toast({
        title: 'Ralat',
        description: 'Sila log masuk untuk menggunakan ciri AI',
        variant: 'destructive',
      });
      return false;
    }

    // If credit has been checked and has sufficient tokens
    if (hasChecked && availableTokens > 0) {
      return true;
    }

    // Check tokens
    const hasCredits = await checkTokens(user.id);
    
    // If no credits, show warning
    if (!hasCredits && !showingWarning) {
      setShowingWarning(true);
      toast({
        title: 'Token AI tidak mencukupi',
        description: 'Sila tingkatkan pelan atau tambah nilai token.',
        variant: 'destructive',
      });
      // Reset warning state after 3 seconds
      setTimeout(() => setShowingWarning(false), 3000);
    }
    
    return hasCredits;
  }, [user, availableTokens, checkTokens, hasChecked, showingWarning]);

  /**
   * Wrapper function for AI actions - only executes if user has credits
   * @param action Function to execute if user has credits
   * @returns Promise resolving to the action result or undefined
   */
  const withCredit = useCallback(async <T>(action: () => Promise<T>): Promise<T | undefined> => {
    const hasCredits = await checkCredit();
    
    if (hasCredits) {
      return action();
    }
    
    return undefined;
  }, [checkCredit]);

  return {
    checkCredit,
    withCredit,
    hasCredits: hasChecked && availableTokens > 0,
    isChecking: isLoading,
    availableTokens
  };
}; 